import React from 'react';
import PropTypes from 'prop-types';
import { LuFolderKanban } from 'react-icons/lu';
import Badge from '../../../../components/ui/Badge';
import useProjectFinanceText from '../projectFinanceText';
import { phaseGroupOf } from '../projectFinanceColumns';

const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';
const PHASE_TONES = { active: 'success', passive: 'neutral', starting: 'info', other: 'neutral' };

export const PHASE_GRADIENT = {
  active: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30',
  passive: 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30',
  starting: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30',
  other: 'tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30',
};

/** The project's phase (stored value, translated when known). */
export const PhaseBadge = ({ phase }) => {
  const { phaseLabel } = useProjectFinanceText();
  if (!phase) return <span className={MUTED}>-</span>;
  return (
    <Badge tone={PHASE_TONES[phaseGroupOf(phase)]}>
      <span aria-hidden="true" className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-current" />
      {phaseLabel(phase)}
    </Badge>
  );
};

PhaseBadge.propTypes = { phase: PropTypes.string };

/** Plain text of a cell (dates in the UI language when they parse). */
export const useProjectCellText = () => {
  const { formatDate, phaseLabel } = useProjectFinanceText();
  return (project, key, type) => {
    const value = project[key];
    if (!value) return '';
    if (key === 'projectPhase') return phaseLabel(value);
    if (type === 'date' && /^\d{4}-\d{2}-\d{2}/.test(value)) return formatDate(value);
    return String(value);
  };
};

/** Content of one project table cell. */
const ProjectFinanceCell = ({ columnKey, project, type }) => {
  const cellText = useProjectCellText();
  if (columnKey === 'projectPhase') return <PhaseBadge phase={project.projectPhase} />;
  if (columnKey === 'titleShort') {
    return (
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
        <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${PHASE_GRADIENT[phaseGroupOf(project.projectPhase)]}`}>
          <LuFolderKanban className="tw-h-3.5 tw-w-3.5" />
        </span>
        <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{project.titleShort || '-'}</span>
      </span>
    );
  }
  const text = cellText(project, columnKey, type);
  return text ? <span className="tw-block tw-truncate">{text}</span> : <span className={MUTED}>-</span>;
};

ProjectFinanceCell.propTypes = { columnKey: PropTypes.string.isRequired, project: PropTypes.object.isRequired, type: PropTypes.string };

/** One row of the card list (narrow screens). */
export const ProjectFinanceCardRow = ({ project }) => (
  <>
    <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${PHASE_GRADIENT[phaseGroupOf(project.projectPhase)]}`}>
      <LuFolderKanban className="tw-h-[18px] tw-w-[18px]" />
    </span>
    <span className="tw-min-w-0 tw-flex-1">
      <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{project.titleShort || '-'}</span>
        <PhaseBadge phase={project.projectPhase} />
      </span>
      <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">
        {[project.customer, project.projectDepartment].filter(Boolean).join(' · ') || '-'}
      </span>
    </span>
  </>
);

ProjectFinanceCardRow.propTypes = { project: PropTypes.object.isRequired };

export default ProjectFinanceCell;
